import { useCallback, useEffect, useState } from 'react';
import { apiFetch, apiWrite } from '../../lib/api-client.js';

interface PendingResponse {
  error?: string;
  requests?: unknown[];
}

interface ReviewPayload {
  request_id: string | number;
  action: string;
  reason?: string;
}

export function usePendingRegistrations(enabled: boolean) {
  const [requests, setRequests] = useState<unknown[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submittingId, setSubmittingId] = useState<string | number | null>(null);

  const loadPending = useCallback(async () => {
    if (!enabled) {
      setRequests([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await apiFetch<PendingResponse>('/api/registration/pending');

      if (data.error) {
        setError(data.error);
        setRequests([]);
        return;
      }

      setRequests(data.requests || []);
    } catch (err) {
      setError((err as Error).message || 'Pending registration load failed');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    let active = true;

    async function run() {
      if (!active) return;
      await loadPending();
    }

    run();
    return () => {
      active = false;
    };
  }, [loadPending]);

  async function reviewRequest(requestId: string | number, action: string, reason = '') {
    setSubmittingId(requestId);
    setError(null);

    try {
      const payload: ReviewPayload = { request_id: requestId, action };
      if (action === 'reject') payload.reason = reason.trim();

      const result = await apiWrite<{ error?: string }>('/api/registration/review', 'POST', payload);
      if (!result.ok) {
        setError(result.data?.error || 'Registration review failed');
        return { ok: false, error: result.data?.error || 'Registration review failed' };
      }

      await loadPending();
      return { ok: true };
    } catch (err) {
      const message = (err as Error).message || 'Registration review failed';
      setError(message);
      return { ok: false, error: message };
    } finally {
      setSubmittingId(null);
    }
  }

  return {
    requests,
    loading,
    error,
    submittingId,
    reviewRequest,
    reload: loadPending,
  };
}
